// File: src/components/EventCard.jsx

import React from 'react';
import {
  IoArrowForward,
  IoPeopleOutline,
} from 'react-icons/io5';
import {
  LuCalendar,
  LuCoffee,
  LuMapPin,
  LuMartini,
  LuMountain,
  LuStar,
} from 'react-icons/lu';

import { useNavigate } from 'react-router-dom';
import soldOutBadge from '../assets/SoldOutBadge.svg';

/* ─────────────────────────────────────────────
   Fallback avatars
───────────────────────────────────────────── */
const FAKE_AVATAR_POOL = [
  'https://i.pinimg.com/1200x/ad/c7/ae/adc7ae6697026c600409215d7e63f1d0.jpg',
  'https://i.pinimg.com/736x/33/6c/97/336c9766a02b5e2158b64ce2d76b2ccc.jpg',
  'https://i.pinimg.com/1200x/ad/20/1e/ad201e26367b77453456fb33173d5e6c.jpg',
  'https://i.pinimg.com/736x/2d/11/dd/2d11ddba02f525ff760a27ed4b875502.jpg',
  'https://i.pinimg.com/vwebp/1200x/ff/b5/22/ffb5223b4deb58189e13c5c17ada537b.webp',
  'https://i.pinimg.com/736x/80/cf/8f/80cf8f4cfc8a18687a41d72d3ba47f64.jpg',
  'https://i.pinimg.com/vwebp/1200x/dd/21/f8/dd21f8e70c9f53089d2317f11579ab6f.webp',
  'https://i.pinimg.com/1200x/ee/92/31/ee92310874c458f4e147cb45046e5da9.jpg',
  'https://i.pinimg.com/736x/d2/b9/61/d2b96101ed0ceca5db68d341bd7267b0.jpg',
  'https://i.pinimg.com/736x/4d/ab/6c/4dab6cede3fa87ef9d6a93135a56470d.jpg',
  'https://i.pinimg.com/736x/0d/a0/45/0da045e2967fc5e579d10f84c4a531b9.jpg',
  'https://i.pinimg.com/736x/f9/bb/73/f9bb73b53f121b9a5a3b612b719568f1.jpg',
  'https://i.pinimg.com/736x/8a/c5/ea/8ac5ea3db6814e90172e3f2f296c72d1.jpg',
  'https://i.pinimg.com/vwebp/1200x/a2/d1/ec/a2d1ecd00d380c4101a7cfb9bd353555.webp',
  'https://i.pinimg.com/736x/ff/61/1a/ff611a9454248dfb297b3a17fe596675.jpg',
  'https://i.pinimg.com/vwebp/736x/32/e0/f9/32e0f9c606b10e5ed8884ba16233fc11.webp',
  'https://i.pinimg.com/736x/15/4c/ae/154cae9beb41dba35b478f37e2228596.jpg',
];

function hashString(value = "") {
  return String(value)
    .split("")
    .reduce(
      (hash, character) =>
        (hash * 31 +
          character.charCodeAt(0)) >>>
        0,
      0
    );
}

function getEventAvatarIndexes(
  eventId = "",
  count = 3
) {
  const poolSize = FAKE_AVATAR_POOL.length;

  if (!poolSize) return [];

  const start =
    hashString(eventId) % poolSize;

  const step =
    poolSize > 3
      ? 5
      : 1;

  return Array.from(
    { length: count },
    (_, index) =>
      (start + index * step) % poolSize
  );
}

function getAvatar(
  user,
  index = 0,
  eventId = ""
) {
  const eventIndexes =
    getEventAvatarIndexes(eventId, 3);

  const fallbackIndex =
    eventIndexes[index] ??
    (
      hashString(`${eventId}-${index}`) %
      FAKE_AVATAR_POOL.length
    );

  if (!user || typeof user === "string") {
    return FAKE_AVATAR_POOL[
      fallbackIndex
    ];
  }

  const person =
    user?.user ||
    user?.member ||
    user;

  const raw =
    person?.profilePicUrl ||
    person?.profilePic ||
    person?.profilePictureUrl ||
    person?.imageUrl ||
    person?.avatarUrl ||
    person?.avatar ||
    person?.photoUrl ||
    person?.photo ||
    "";

  if (!raw) {
    return FAKE_AVATAR_POOL[
      fallbackIndex
    ];
  }

  return raw.startsWith("http")
    ? raw
    : `${process.env.REACT_APP_API_URL}${raw}`;
}
function normalizeImage(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;

  if (typeof value === 'object') {
    return (
      value.url ||
      value.src ||
      value.imageUrl ||
      value.secure_url ||
      value.path ||
      null
    );
  }

  return null;
}

function getEventImages(event) {
  const raw = [
    ...(Array.isArray(event?.galleryImages) ? event.galleryImages : []),
    ...(Array.isArray(event?.images) ? event.images : []),
    ...(Array.isArray(event?.photos) ? event.photos : []),
    event?.image,
    event?.coverImage,
    event?.imageUrl,
  ];

  return Array.from(new Set(raw.map(normalizeImage).filter(Boolean))).slice(0, 3);
}
/* ─────────────────────────────────────────────
   Date helpers
───────────────────────────────────────────── */
function getShortWeekday(date) {
  return date.toLocaleString('en-US', { weekday: 'short' });
}

function getMonthDay(date) {
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateAndTime(eventDate, startTime12, endTime12) {
  if (!eventDate && !startTime12) return 'Date TBD';

  try {
    const [yyyy, mm, dd] = eventDate.split('-');
    const dateObj = new Date(+yyyy, +mm - 1, +dd);
    const dateLabel = `${getShortWeekday(dateObj)}, ${getMonthDay(dateObj)}`;

    if (startTime12) {
  return `${dateLabel} · ${startTime12}`;
}
    if (startTime12) {
      return `${dateLabel}, ${startTime12}`;
    }

    return dateLabel;
  } catch {
    if (startTime12 && endTime12) {
      return `${eventDate || 'Date TBD'}, ${startTime12} - ${endTime12}`;
    }

    if (startTime12) {
      return `${eventDate || 'Date TBD'}, ${startTime12}`;
    }

    return eventDate || 'Date TBD';
  }
}

/* ─────────────────────────────────────────────
   Event helpers
───────────────────────────────────────────── */
function getAttendees(event) {
  return Array.isArray(event?.attendees) ? event.attendees : [];
}

function getCapacity(event) {
  const displayCapacity = Number(event?.displayCapacity);
  const capacity = Number(event?.capacity);

  if (Number.isFinite(displayCapacity) && displayCapacity > 0) return displayCapacity;
  if (Number.isFinite(capacity) && capacity > 0) return capacity;

  return 0;
}

function getAttendeeCount(event) {
  const displayCount = Number(event?.displayAttendeeCount);
  const attendeeCount = Number(event?.attendeeCount);

  if (Number.isFinite(displayCount)) return displayCount;
  if (Number.isFinite(attendeeCount)) return attendeeCount;

  return getAttendees(event).length;
}

function getHostName(event) {
  return (
    event?.promoterId?.name ||
    event?.hostedBy?.name ||
    event?.host?.name ||
    event?.hostName ||
    'Tribe'
  );
}

function toFirstLastInitial(name = '') {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return 'Host';

  const first = parts[0];
  const last =
    parts.length > 1
      ? parts[parts.length - 1]
      : '';

  return last
    ? `${first} ${last.charAt(0)}.`
    : first;
}

function getHostAvatar(event) {
  const raw =
    event?.promoterId?.imageUrl ||
    event?.promoterId?.profilePicUrl ||
    event?.hostedBy?.imageUrl ||
    event?.host?.imageUrl ||
    '';

  if (!raw) return null;

  return raw.startsWith('http')
    ? raw
    : `${process.env.REACT_APP_API_URL}${raw}`;
}


function formatHostSince(dateValue) {
  if (!dateValue) return 'Hosted social experience';

  try {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'Hosted social experience';

    return `Hosting since ${date.toLocaleString('en-US', {
      month: 'short',
      year: 'numeric',
    })}`;
  } catch {
    return 'Hosted social experience';
  }
}

function getHostedAtLabel(event) {
  return (
    event?.hostedAt ||
    event?.locationName ||
    event?.neighborhood ||
    event?.city ||
    'Los Angeles'
  );
}

function getOneEventTag(event) {
  if (event?.purposeTag) return event.purposeTag;

  const rawTags = event?.categoryIds || event?.categories || event?.tags || [];

  if (Array.isArray(rawTags) && rawTags.length > 0) {
    const first = rawTags[0];

    if (typeof first === 'string') return first;
    return first?.label || first?.name || first?.id || null;
  }

  if (event?.mode && event.mode !== 'standard') return event.mode;

  return 'Social';
}

function cleanTag(tag) {
  return String(tag || '')
    .replace(/-/g, ' ')
    .trim();
}

function getEventType(event) {
  const raw =
    event?.eventType ||
    event?.purposeTag ||
    event?.type ||
    event?.mode ||
    '';

  const value = String(raw).toLowerCase();

  if (value.includes('date') || value.includes('dating')) return 'Dating';
  if (value.includes('network') || value.includes('professional')) return 'Networking';
  if (value.includes('friend')) return 'Friendship';

  return 'Friendship';
}

function getEventTypeBadge(type) {
  const value = String(type || '').toLowerCase();

  if (value.includes('dating')) {
    return {
      emoji: '💘',
      label: 'Dating',
      style: {
        background: 'rgba(255, 87, 126, 0.12)',
        color: '#B91C4A',
        border: '1px solid rgba(255, 87, 126, 0.22)',
      },
    };
  }

  if (value.includes('network')) {
    return {
      emoji: '🤝',
      label: 'Networking',
      style: {
        background: 'rgba(37, 99, 235, 0.10)',
        color: '#1D4ED8',
        border: '1px solid rgba(37, 99, 235, 0.18)',
      },
    };
  }

  return {
    emoji: '👋',
    label: 'Friendship',
    style: {
      background: 'rgba(34, 197, 94, 0.11)',
      color: '#15803D',
      border: '1px solid rgba(34, 197, 94, 0.18)',
    },
  };
}



function getVibeBadge(vibeTag) {
  const value = String(vibeTag || '').trim();

  if (value === 'Night Out') {
    return {
      label: 'Night Out',
      icon: (
        <LuMartini
          size={13}
          strokeWidth={2.2}
        />
      ),
    };
  }

  if (value === 'Adventure') {
    return {
      label: 'Adventure',
      icon: (
        <LuMountain
          size={13}
          strokeWidth={2.2}
        />
      ),
    };
  }

  if (value === 'Chill') {
    return {
      label: 'Chill',
      icon: (
        <LuCoffee
          size={13}
          strokeWidth={2.2}
        />
      ),
    };
  }

  return null;
}
function truncate(text, max = 112) {
  if (!text) {
    return 'A small hosted gathering designed to make meeting new people feel natural.';
  }

  if (text.length <= max) return text;

  return `${text.slice(0, max).trim()}...`;
}



function renderTitleWithBracketWeight(title) {
  const text = String(title || '');
  const parts = text.split(/(\[[^\]]+\])/g);

  return parts.map((part, index) => {
    const isBracket = /^\[[^\]]+\]$/.test(part);

    if (!isBracket) {
      return <React.Fragment key={index}>{part}</React.Fragment>;
    }

    return (
      <span key={index} style={styles.titleBracket}>
        {part}
      </span>
    );
  });
}

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */

function isUserReservedForEvent(event, userId) {
  if (!event || !userId) return false;

  const uid = String(userId);

  const attendeeIds = Array.isArray(event?.attendeeIds)
    ? event.attendeeIds
    : [];

  const attendees = Array.isArray(event?.attendees)
    ? event.attendees
    : [];

  return (
    attendeeIds.some((id) => String(id?._id || id) === uid) ||
    attendees.some((attendee) => String(attendee?._id || attendee) === uid)
  );
}



export default function EventCard({
  event,
  isFocused,
  userId,
  isRecommended = false,
  live = false,
  upcoming = false,
  landing = false,
}) {

  const navigate = useNavigate();

  if (!event?._id) return null;

  const {
  _id,
  name,
  title,
  description,
  eventDate,
  startTime12,
  endTime12,
} = event;

  const eventTitle = name || title || 'Tribe gathering';
const hostName = toFirstLastInitial(
  getHostName(event)
);
const hostAvatar = getHostAvatar(event);
const hostSinceLabel = formatHostSince(event?.promoterId?.hostSince);
const hostedAtLabel = getHostedAtLabel(event);

const dateTimeLabel = formatDateAndTime(eventDate, startTime12, endTime12);
const eventType = getEventType(event);
const eventTypeBadge = getEventTypeBadge(eventType);

const vibeBadge = getVibeBadge(
  event?.vibeTag
);



const eventImages = getEventImages(event);

  const attendees = getAttendees(event);
  const attendeeCount = getAttendeeCount(event);
  const capacity = getCapacity(event);
const spotsLeft =
  capacity > 0
    ? Math.max(capacity - attendeeCount, 0)
    : null;

const capacityLabel =
  spotsLeft !== null
    ? `${spotsLeft} of ${capacity}`
    : `${attendeeCount}+`;

  const isSoldOut =
  Boolean(event?.isSoldOut) ||
  (capacity > 0 && attendeeCount >= capacity);

const isReserved = isUserReservedForEvent(event, userId);

const isLive = live;

const isMixer = event.mode === 'mixer' || event.type === 'mixer';
  const previewId = isMixer ? `mixer-${_id}` : _id;

  const goToPreview = () => {
  navigate(`/events/${previewId}`, {
    state: {
      event,
      isRecommended,
    },
  });
};

  return (
   <li
  id={`eventcard_${_id}`}
  style={{
    ...styles.card,
    ...(landing ? styles.landingCard : {}),
    ...(isFocused ? styles.focusedCard : {}),
  }}
  onClick={goToPreview}
>

  <style>
  {`

  #eventcard_${_id} .tribe-badge-row::-webkit-scrollbar {
  display: none;
}
    @keyframes tribeLivePulse {
      0% {
        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.45);
        transform: scale(1);
      }

      70% {
        box-shadow: 0 0 0 8px rgba(34, 197, 94, 0);
        transform: scale(1.06);
      }

      100% {
        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
        transform: scale(1);
      }
    }
  `}
</style>
      {isSoldOut && (
        <img
          src={soldOutBadge}
          alt="Sold out"
          style={styles.soldOutBadge}
        />
      )}

      <div style={styles.hostRow}>
        <div style={styles.hostAvatar}>
          {hostAvatar ? (
            <img src={hostAvatar} alt={hostName} style={styles.hostAvatarImg} />
          ) : (
            <span style={styles.hostAvatarText}>tribe</span>
          )}
        </div>

        <div style={styles.hostCopy}>
<div style={styles.hostTitle}>
  Hosted by <span style={styles.hostName}>{hostName}</span>
</div>         
<div style={styles.hostSub}>{hostSinceLabel}</div>
        </div>
      </div>

<h2 style={styles.title}>
  {renderTitleWithBracketWeight(eventTitle)}
</h2>

      <div style={styles.infoStack}>
  <InfoRow icon={<LuMapPin />} text={hostedAtLabel} />

  <InfoRow icon={<LuCalendar />} text={dateTimeLabel} />
</div>

<div
  className="tribe-badge-row"
  style={styles.badgeRow}
>
    {isRecommended && (
  <Badge
    icon={
      <LuStar
        size={13}
        strokeWidth={2.4}
      />
    }
    label="Picked for you"
    customStyle={styles.recommendedBadge}
  />
)}

  {isReserved && (
    <Badge
      emoji="🎟️"
      label="Reserved"
      customStyle={styles.reservedBadge}
    />
  )}

<Badge
  emoji={eventTypeBadge.emoji}
  label={eventTypeBadge.label}
  customStyle={eventTypeBadge.style}
/>

{vibeBadge && (
  <Badge
    icon={vibeBadge.icon}
    label={vibeBadge.label}
    customStyle={styles.vibeBadge}
  />
)}
</div>

{description && (
  <p style={styles.descriptionPreview}>
    {description}
  </p>
)}
     <div
  style={{
    ...styles.footer,
    ...(landing ? styles.landingFooter : {}),
  }}
>
  {!landing && (
    <>
      <div style={styles.socialProof}>
        {isLive ? (
          <div style={styles.liveProof}>
            <span style={styles.liveDot} />
            <span style={styles.liveText}>
              LIVE
            </span>
          </div>
        ) : (
          <>
            <div style={styles.avatarRow}>
              {[0, 1, 2].map((slot) => {
                const attendee =
                  attendees[slot];

                return (
                  <div
                    key={slot}
                    style={{
                      ...styles.avatar,
                      marginLeft:
                        slot === 0
                          ? 0
                          : -18,
                      backgroundImage: `url(${getAvatar(
                        attendee,
                        slot,
                        _id
                      )})`,
                    }}
                  />
                );
              })}
            </div>

            <div style={styles.reservedCopy}>
              <div style={styles.reservedCount}>
                {capacityLabel}
              </div>

              <div style={styles.reservedLabel}>
                {spotsLeft !== null
                  ? 'spots remaining'
                  : 'people going'}
              </div>
            </div>
          </>
        )}
      </div>

      <div style={styles.footerDivider} />
    </>
  )}

  <button
    type="button"
    style={{
      ...styles.reserveButton,
      ...(landing
        ? styles.landingReserveButton
        : {}),
    }}
    onClick={(e) => {
      e.stopPropagation();
      goToPreview();
    }}
  >
    <span>See details</span>
    <IoArrowForward size={16} />
  </button>
</div>
    </li>
  );
}

function InfoRow({ icon, text }) {
  return (
    <div style={styles.infoRow}>
      <span style={styles.infoIcon}>{icon}</span>
      <span style={styles.infoText}>{text}</span>
    </div>
  );
}

function Badge({
  emoji,
  icon,
  label,
  customStyle,
}) {
  return (
    <div style={{ ...styles.badge, ...customStyle }}>
      {icon && (
        <span style={styles.badgeIcon}>
          {icon}
        </span>
      )}

      {!icon && emoji && (
        <span style={styles.badgeEmoji}>
          {emoji}
        </span>
      )}

      <span>{label}</span>
    </div>
  );
}
/* ─────────────────────────────────────────────
   Styles
───────────────────────────────────────────── */
const styles = {
  card: {
    listStyle: 'none',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    border: '1px solid rgba(17,17,17,0.08)',
    borderRadius: 12,
    padding: 20,
    paddingBottom: 30,
    paddingTop: 30,
    marginBottom: 8,
    overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: '0 18px 44px rgba(0,0,0,0.07)',
    transition:
      'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  focusedCard: {
    border: '1px solid #4285F4',
    boxShadow:
      '0 0 0 4px rgba(66,133,244,0.12), 0 18px 44px rgba(0,0,0,0.1)',
    transform: 'scale(1.012)',
    zIndex: 20,
  },

  landingCard: {
  boxShadow: 'none',
},

  soldOutBadge: {
    position: 'absolute',
    top: 10,
    right: 0,
    zIndex: 30,
    width: 72,
    height: 'auto',
    pointerEvents: 'none',
  },

  hostRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 18,
  },

  hostAvatar: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: '#050505',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
  },

  hostAvatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },

  hostAvatarText: {
    fontSize: '0.9rem',
    fontWeight: 900,
    letterSpacing: '-0.06em',
    textTransform: 'lowercase',
  },

  hostCopy: {
    minWidth: 0,
  },

  hostTitle: {
    color: '#000',
    fontSize: '0.85rem',
    lineHeight: 1.1,
    fontWeight: 500,
    letterSpacing: '-0.028em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  hostName: {
  fontWeight: 800,
  color: '#000',
},
  titleBracket: {
  fontWeight: 400,
  color: 'rgba(7,7,7,0.78)',
  letterSpacing: '-0.055em',
},

  hostSub: {
    marginTop: 0,
    color: 'rgba(17,17,17,0.48)',
    fontSize: '0.8rem',
    lineHeight: 1.1,
    fontWeight: 560,
    letterSpacing: '-0.018em',
  },

title: {
  margin: 0,
  marginTop: -4,
  color: '#000',
  fontSize: '1.4rem',
  lineHeight: 1.2,
  fontWeight: 700,
  maxWidth: '100%',

  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
},

  infoStack: {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 14,
},

infoRow: {
  display: 'grid',
  gridTemplateColumns: '18px minmax(0, 1fr)',
  alignItems: 'center',
  gap: 9,
  minWidth: 0,
},

infoIcon: {
  width: 18,
  height: 18,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(17,17,17,0.72)',
  fontSize: 19,
},

infoText: {
  minWidth: 0,
  color: "rgba(17,17,17,0.68)",
  fontSize: '0.85rem',
  lineHeight: 1.25,
  fontWeight: 520,
  letterSpacing: '-0.022em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
},


  badgeRow: {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginTop: 14,
  flexWrap: 'nowrap',

  width: '100%',
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'hidden',

  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
},

badge: {
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  gap: 6,
  borderRadius: 999,
  padding: '8px 6px',
  fontSize: '0.65rem',
  lineHeight: 1,
  fontWeight: 760,
  letterSpacing: '-0.025em',
  boxShadow: '0 8px 20px rgba(0,0,0,0.025)',
  whiteSpace: 'nowrap',
},

limitedBadge: {
  background: '#f59e0b21',
  color: '#B45309',
  border: '1px solid rgba(245, 158, 11, 0.22)',
},

recommendedBadge: {
background: "#FFF6CC",
  color: "#000",
  border: '1px solid rgba(17,17,17,0.1)',
  paddingRight: '8px',
},
reservedBadge: {
  background: '#B91C1C',
  color: '#fff',
  border: '1px solid rgba(220, 38, 38, 0.2)',
  paddingRight: '8px',

},

badgeEmoji: {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  lineHeight: 1,
},

  badgeIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
  },

  description: {
    margin: '22px 0 0',
    color: 'rgba(17,17,17,0.58)',
    fontSize: '0.92rem',
    lineHeight: 1.42,
    fontWeight: 450,
    letterSpacing: '-0.014em',
  },

  footer: {
    marginTop: 28,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 1px auto',
    alignItems: 'center',
    gap: 18,
  },

  socialProof: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: 12,
  },

  avatarRow: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },

  avatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    backgroundColor: '#ddd',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: '2px solid #F7E8E1',
    boxSizing: 'border-box',
  },

  reservedCopy: {
    minWidth: 0,
  },

  reservedCount: {
    color: '#050505',
    fontSize: '.75rem',
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: '-0.04em',
  },

reservedLabel: {
  marginTop: 4,
  color: 'rgba(17,17,17,0.48)',
  fontSize: '0.74rem',
  lineHeight: 1,
  fontWeight: 600,
  letterSpacing: '-0.018em',
  textTransform: 'none',
  whiteSpace: 'nowrap',
},


  reserveButton: {
    border: '1px solid #111',
    background: 'transparent',
    color: '#111',
    borderRadius: 999,
    padding: '8px 12px',
    minWidth: 90,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    fontSize: '0.75rem',
    lineHeight: 1,
    fontWeight: 760,
    letterSpacing: '-0.026em',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
miniImageRow: {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 8,
  marginTop: 14,
  width: '100%',
},

miniEventImage: {
  width: '100%',
  height: 80,
  objectFit: 'cover',
  display: 'block',
  borderRadius: 8,
  border: '1.5px solid #ccc',
  backgroundColor: '#f1f1f1',
},


liveProof: {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 36,
  padding: '0 2px',
},

liveDot: {
  width: 12,
  height: 12,
  borderRadius: '50%',
  background: '#22C55E',
  animation: 'tribeLivePulse 1.35s ease-out infinite',
  flexShrink: 0,
},

liveText: {
  color: '#15803D',
  fontSize: '0.85rem',
  lineHeight: 1,
  fontWeight: 900,
  letterSpacing: '0.06em',
},


footer: {
  marginTop: 28,
  display: 'grid',
  gridTemplateColumns:
    'minmax(0, 1fr) 1px auto',
  alignItems: 'center',
  gap: 18,
},

footerDivider: {
  width: 1,
  height: 34,
  justifySelf: 'center',
  background: 'rgba(17,17,17,0.08)',
},

descriptionPreview: {
  margin: "16px 0 0",
  color: "rgba(17,17,17,0.68)",
fontSize: "0.84rem",
lineHeight: 1.4,
  fontWeight: 450,
  letterSpacing: "-0.018em",

  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
},

vibeBadge: {
  background: "#F4F4F5",
  color: "#27272A",
  border:
    "1px solid rgba(17,17,17,0.08)",
  paddingLeft: "9px",
  paddingRight: "9px",
},


landingFooter: {
  gridTemplateColumns: 'auto',
  justifyContent: 'start',
  gap: 0,
},

landingReserveButton: {
  justifySelf: 'start',
},

};